/**
 * AgentSearchCommandDialog component - Command palette for agent chat navigation
 * Shows search input, new chat option, and recent chat history grouped by date
 */

import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { MessageSquare, Plus } from "lucide-react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog"
import { useAgentChatHistory } from "@/hooks/use-agent-chat-history"

/**
 * Group labels for display in the command dialog
 * Maps internal group keys to user-friendly labels
 */
const GROUP_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  lastWeek: 'Last 7 days',
  lastMonth: 'Last 30 days',
  older: 'Older',
}

/**
 * Group order for rendering chat history sections
 * Defines the sequence in which groups appear in the dialog
 */
const GROUP_ORDER = ['today', 'yesterday', 'lastWeek', 'lastMonth', 'older']

/**
 * Props for the AgentSearchCommandDialog component
 * Controls dialog visibility and state changes
 */
interface AgentSearchCommandDialogProps {
  /**
   * Whether the dialog is currently open
   * Controls visibility of the command palette
   */
  open: boolean
  /**
   * Callback when open state changes
   * Called when dialog should open or close
   */
  onOpenChange: (open: boolean) => void
  /**
   * Optional agent ID override (when used outside agent route context)
   */
  agentId?: string
}

/**
 * Preserves embed URL parameters when navigating
 * Ensures API key and embed parameters are maintained during navigation
 *
 * @param {string} basePath - The base path to navigate to
 * @returns {string} Path with preserved embed parameters appended
 */
function getUrlWithEmbedParams(basePath: string): string {
  const urlParams = new URLSearchParams(window.location.search)
  const embedParams = new URLSearchParams()

  if (urlParams.get('apikey')) {
    embedParams.set('apikey', urlParams.get('apikey')!)
  }
  if (urlParams.get('embed')) {
    embedParams.set('embed', urlParams.get('embed')!)
  }
  if (urlParams.get('bubble')) {
    embedParams.set('bubble', urlParams.get('bubble')!)
  }

  return embedParams.toString() ? `${basePath}?${embedParams.toString()}` : basePath
}

/**
 * AgentSearchCommandDialog component
 * Command palette for quick navigation with search and recent agent chats grouped by date
 *
 * @param {AgentSearchCommandDialogProps} props - Component props
 * @returns {JSX.Element} The command dialog for agent chat search
 */
export function AgentSearchCommandDialog({ open, onOpenChange, agentId: agentIdProp }: AgentSearchCommandDialogProps) {
  const navigate = useNavigate()
  const { agentId: agentIdParam, appId } = useParams<{ agentId: string; appId?: string }>()
  const agentId = agentIdProp || agentIdParam
  const pathPrefix = appId ? `/app/${appId}/agents` : "/agent"

  /**
   * Hook for fetching agent chat history
   * Provides grouped data for display in the command palette
   */
  const {
    chatHistory,
    groupedData,
    isLoading,
    fetchChatHistory,
  } = useAgentChatHistory()

  /**
   * Load chat history when dialog opens
   * Fetches fresh data each time the dialog is opened
   */
  React.useEffect(() => {
    if (open && agentId) {
      fetchChatHistory(agentId)
    }
  }, [open, agentId, fetchChatHistory])

  /**
   * Handles new chat action
   * Closes dialog and navigates to new chat page
   */
  const handleNewChat = () => {
    onOpenChange(false)
    if (agentId) {
      navigate(getUrlWithEmbedParams(`${pathPrefix}/${agentId}/chat`))
    }
  }

  /**
   * Handles chat item selection
   * Closes dialog and navigates to selected chat
   *
   * @param {string} chatId - The ID of the selected chat
   */
  const handleSelectChat = (chatId: string) => {
    onOpenChange(false)
    if (agentId) {
      navigate(getUrlWithEmbedParams(`${pathPrefix}/${agentId}/chat/${chatId}`))
    }
  }

  /**
   * Check if there are any chats to display
   * Used to show empty state when no chats exist
   */
  const hasChats = chatHistory.length > 0

  /**
   * Renders a single chat item in the command list
   * Displays chat title with message icon
   *
   * @param {any} chat - The chat object to render
   * @returns {JSX.Element} The rendered chat item
   */
  const renderChatItem = (chat: any) => (
    <CommandItem
      key={chat.id}
      value={chat.title}
      onSelect={() => handleSelectChat(chat.id)}
      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
    >
      <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <div className="flex flex-1 items-center gap-2 min-w-0">
        <span className="truncate">{chat.title}</span>
      </div>
    </CommandItem>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-lg">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:py-1.5">
          <CommandInput placeholder="Search chats..." />
          <CommandList className="max-h-[400px]">
            <CommandEmpty>No results found.</CommandEmpty>

            {/* New Chat Action */}
            <CommandGroup>
              <CommandItem
                onSelect={handleNewChat}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer"
              >
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">New Chat</span>
              </CommandItem>
            </CommandGroup>

            {/* Loading State */}
            {isLoading && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            )}

            {/* No Chats State */}
            {!isLoading && !hasChats && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No recent chats
              </div>
            )}

            {/* Grouped Chat History */}
            {!isLoading && groupedData && GROUP_ORDER.map(groupKey => {
              const chats = (groupedData as any)[groupKey]
              if (!chats || chats.length === 0) return null

              return (
                <CommandGroup key={groupKey} heading={GROUP_LABELS[groupKey]}>
                  {chats.map(renderChatItem)}
                </CommandGroup>
              )
            })}

            {/* Fallback flat list if no grouped data */}
            {!isLoading && !groupedData && hasChats && (
              <CommandGroup heading="Recent Chats">
                {chatHistory.slice(0, 10).map(renderChatItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
